import { useState, useEffect } from "react";

const defaultParams = {
  alcohol:11.0, fixed_acidity:7.4, volatile_acidity:0.45, citric_acid:0.3,
  residual_sugar:3.0, chlorides:0.085, free_sulfur_dioxide:20,
  total_sulfur_dioxide:70, pH:3.25, density:0.997, sulphates:0.65,
};
const paramLabels = {
  alcohol:             {label:"Alcool",             unit:"%",    min:8,    max:15,    step:0.1,   dp:1},
  fixed_acidity:       {label:"Aciditate fixă",     unit:"g/L",  min:4,    max:16,    step:0.1,   dp:1},
  volatile_acidity:    {label:"Aciditate volatilă", unit:"g/L",  min:0.1,  max:1.2,   step:0.01,  dp:2},
  citric_acid:         {label:"Acid citric",        unit:"g/L",  min:0,    max:1,     step:0.01,  dp:2},
  residual_sugar:      {label:"Zahăr rezidual",     unit:"g/L",  min:1,    max:25,    step:0.1,   dp:1},
  chlorides:           {label:"Cloruri",            unit:"g/L",  min:0.01, max:0.2,   step:0.001, dp:3},
  free_sulfur_dioxide: {label:"SO₂ liber",          unit:"mg/L", min:1,    max:72,    step:1,     dp:0},
  total_sulfur_dioxide:{label:"SO₂ total",          unit:"mg/L", min:6,    max:290,   step:1,     dp:0},
  pH:                  {label:"pH",                 unit:"",     min:2.7,  max:4.0,   step:0.01,  dp:2},
  density:             {label:"Densitate",          unit:"g/cm³",min:0.99, max:1.004, step:0.0001,dp:4},
  sulphates:           {label:"Sulfați",            unit:"g/L",  min:0.3,  max:2.0,   step:0.01,  dp:2},
};
const presets = {
  excellent:{label:"Vin excelent", values:{alcohol:12.8,fixed_acidity:6.2,volatile_acidity:0.32,citric_acid:0.48,residual_sugar:2.5,chlorides:0.092,free_sulfur_dioxide:28,total_sulfur_dioxide:92,pH:3.18,density:0.996,sulphates:0.72}},
  average:  {label:"Vin mediu",    values:{alcohol:9.5, fixed_acidity:7.8,volatile_acidity:0.65,citric_acid:0.25,residual_sugar:2.1,chlorides:0.085,free_sulfur_dioxide:15,total_sulfur_dioxide:45,pH:3.25,density:0.997,sulphates:0.65}},
  poor:     {label:"Vin slab",     values:{alcohol:8.2, fixed_acidity:9.5,volatile_acidity:0.85,citric_acid:0.15,residual_sugar:18.5,chlorides:0.110,free_sulfur_dioxide:8, total_sulfur_dioxide:35,pH:2.95,density:0.998,sulphates:0.45}},
};
const BENCHMARK = {
  red:  {alcohol:10.4,fixed_acidity:8.3,volatile_acidity:0.53,citric_acid:0.27,residual_sugar:2.5,chlorides:0.087,free_sulfur_dioxide:16,total_sulfur_dioxide:46,pH:3.31,density:0.9967,sulphates:0.66,avg_score:5.6},
  white:{alcohol:10.5,fixed_acidity:6.9,volatile_acidity:0.28,citric_acid:0.33,residual_sugar:6.4,chlorides:0.046,free_sulfur_dioxide:35,total_sulfur_dioxide:138,pH:3.19,density:0.9940,sulphates:0.49,avg_score:5.9},
  rosé: {alcohol:11.0,fixed_acidity:7.0,volatile_acidity:0.40,citric_acid:0.30,residual_sugar:4.0,chlorides:0.055,free_sulfur_dioxide:25,total_sulfur_dioxide:90,pH:3.25,density:0.9950,sulphates:0.55,avg_score:5.8},
};

const HISTORY_KEY = "vinum_v3";
const loadHistory = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY)||"[]"); } catch { return []; } };
const saveHistory = h => { try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(0,30))); } catch {} };

const G = "#c8a96e", R = "#c05050", GR = "#8fbe6a", AM = "#e8c87a", OR = "#d4956a", BL = "#6ab0c8";
const calCol = {"Slab":R,"Mediocru":OR,"Bun":AM,"Foarte Bun":GR,"Excelent":G};
const scoreCol = s => s>=8?G:s>=6?AM:s>=4?OR:R;

const card = (extra={}) => ({
  background:"#161008", border:"1px solid #2e1f0e", borderRadius:6,
  padding:"16px 20px", ...extra
});

function ScoreBar({score}) {
  const [w,setW] = useState(0);
  useEffect(()=>{ setW(0); const t=setTimeout(()=>setW(score/10*100),150); return()=>clearTimeout(t); },[score]);
  return (
    <div style={{background:"#0a0704",borderRadius:4,height:22,overflow:"hidden",border:"1px solid #3a2a1a",margin:"10px 0 6px"}}>
      <div style={{width:`${w}%`,height:"100%",background:scoreCol(score),transition:"width 1.2s ease",display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:7}}>
        {w>15&&<span style={{fontSize:11,fontWeight:700,color:"#0a0704",fontFamily:"monospace"}}>{score}</span>}
      </div>
    </div>
  );
}

function ShapBar({label,value,maxAbs}) {
  const pos=value>=0, pct=Math.min(Math.abs(value)/maxAbs*100,100);
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
      <span style={{width:152,fontSize:11,color:"#9a8070",textAlign:"right",flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>
      <div style={{flex:1,position:"relative",height:16,background:"#0a0704",borderRadius:2}}>
        <div style={{position:"absolute",left:"50%",top:0,bottom:0,width:1,background:"#3a2a1a"}}/>
        {pos
          ? <div style={{position:"absolute",left:"50%",width:`${pct/2}%`,top:3,bottom:3,background:GR,borderRadius:"0 2px 2px 0"}}/>
          : <div style={{position:"absolute",right:"50%",width:`${pct/2}%`,top:3,bottom:3,background:R,borderRadius:"2px 0 0 2px"}}/>
        }
      </div>
      <span style={{width:44,fontSize:11,fontFamily:"monospace",color:pos?GR:R,textAlign:"right",flexShrink:0}}>{pos?"+":""}{value.toFixed(2)}</span>
    </div>
  );
}

function FermentationSim() {
  const [temp,setTemp]=useState(22), [sugar,setSugar]=useState(180), [data,setData]=useState([]);
  const simulate=()=>{
    const pts=[]; let s=sugar,a=0;
    for(let d=0;d<=14;d++){
      const rate=Math.max(0,0.9*(1-Math.exp(-0.3*d))*Math.exp(-0.05*(temp-22)**2)*(s/200));
      const ds=Math.min(s,rate*18); s-=ds; a+=ds*0.51;
      pts.push({d,sugar:+s.toFixed(1),alcohol:+a.toFixed(2)});
    }
    setData(pts);
  };
  const maxS=sugar;
  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
        {[["Temperatură fermentație",temp,setTemp,14,32,0.5,"°C"],["Zahăr inițial must",sugar,setSugar,100,280,5,"g/L"]].map(([lbl,val,set,mn,mx,st,u])=>(
          <div key={lbl} style={card()}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:11,color:"#9a8070"}}>{lbl}</span>
              <span style={{fontSize:13,color:G,fontFamily:"monospace",fontWeight:600}}>{val}{u}</span>
            </div>
            <input type="range" min={mn} max={mx} step={st} value={val} onChange={e=>set(+e.target.value)}
              style={{width:"100%",cursor:"pointer",accentColor:G,height:4}}/>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#4a3520",marginTop:3}}><span>{mn}{u}</span><span>{mx}{u}</span></div>
          </div>
        ))}
      </div>
      <button onClick={simulate} style={{width:"100%",padding:"12px",background:`linear-gradient(135deg,#7a5020,${G},#7a5020)`,border:"none",color:"#0a0704",fontSize:13,letterSpacing:"0.15em",textTransform:"uppercase",cursor:"pointer",borderRadius:4,fontFamily:"Georgia,serif",fontWeight:700,marginBottom:14}}>
        Simulează Fermentația (14 zile)
      </button>
      {data.length>0&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
            {[["Alcool final estimat",`${data[13].alcohol.toFixed(1)}%`,G],["Zahăr rezidual",`${data[13].sugar.toFixed(1)} g/L`,data[13].sugar<4?GR:AM],["Temp. optimă",temp<18||temp>26?"⚠ Ajustați":"✓ Ideală",temp>=18&&temp<=26?GR:R]].map(([l,v,c])=>(
              <div key={l} style={{background:"#0a0704",borderRadius:4,padding:"10px 12px",border:"1px solid #2e1f0e"}}>
                <p style={{margin:"0 0 3px",fontSize:10,color:"#6a5040"}}>{l}</p>
                <p style={{margin:0,fontSize:16,color:c,fontFamily:"monospace"}}>{v}</p>
              </div>
            ))}
          </div>
          <div style={{...card(),marginBottom:12}}>
            <p style={{margin:"0 0 10px",fontSize:11,color:G,letterSpacing:"0.1em"}}>EVOLUȚIE FERMENTAȚIE — 14 ZILE</p>
            <svg width="100%" viewBox="0 0 540 130" style={{display:"block"}}>
              {[0,32,65,98,130].map(y=><line key={y} x1="35" y1={y} x2="530" y2={y} stroke="#2e1f0e" strokeWidth="0.5"/>)}
              <polyline fill="none" stroke={AM} strokeWidth="2" points={data.map((p,i)=>`${35+i*(495/14)},${130-p.sugar/maxS*115}`).join(" ")}/>
              <polyline fill="none" stroke={GR} strokeWidth="2" points={data.map((p,i)=>`${35+i*(495/14)},${130-p.alcohol/15*115}`).join(" ")}/>
              {[0,7,14].map(d=><text key={d} x={35+d*(495/14)} y="128" fill="#6a5040" fontSize="9" textAnchor="middle">zi {d}</text>)}
              <text x="2" y="10" fill={AM} fontSize="8">{maxS}</text>
              <text x="2" y="128" fill={AM} fontSize="8">0</text>
            </svg>
            <div style={{display:"flex",gap:16,marginTop:6,fontSize:11,color:"#6a5040"}}>
              <span><span style={{color:AM}}>■</span> Zahăr (g/L)</span>
              <span><span style={{color:GR}}>■</span> Alcool (%)</span>
            </div>
          </div>
          <div style={{padding:"10px 14px",background:temp>=18&&temp<=24?"#8fbe6a12":"#c0505012",border:`1px solid ${temp>=18&&temp<=24?GR+"40":R+"40"}`,borderRadius:4,fontSize:12,color:temp>=18&&temp<=24?GR:R}}>
            {temp<18&&"⚠ Temperatură prea scăzută — fermentație lentă, risc oprire. Recomandat: 18-22°C."}
            {temp>=18&&temp<=24&&"✓ Temperatură optimă. Profil aromatic echilibrat, fermentație controlată."}
            {temp>24&&temp<=28&&"⚠ Temperatură ridicată — fermentație rapidă, posibil pierderi de arome fine."}
            {temp>28&&"❗ Temperatură critică — risc oprire fermentație! Răciți imediat la sub 25°C."}
          </div>
        </div>
      )}
    </div>
  );
}

function exportCSV(history) {
  const h = ["ID","Data","Tip Vin","Scor","Calificativ","Pret Min","Pret Max","Vandabil","Alcool","Acid Fixa","Acid Volatila","Acid Citric","Zahar","Cloruri","SO2 Liber","SO2 Total","pH","Densitate","Sulfati","Feedback","Nota Enolog","Decizie Enolog"];
  const rows = history.map((e,i)=>[history.length-i,new Date(e.timestamp).toLocaleDateString("ro-RO"),e.wineType,e.result?.score,e.result?.calificativ,e.result?.pret_min,e.result?.pret_max,e.result?.vandabil?"Da":"Nu",e.params.alcohol,e.params.fixed_acidity,e.params.volatile_acidity,e.params.citric_acid,e.params.residual_sugar,e.params.chlorides,e.params.free_sulfur_dioxide,e.params.total_sulfur_dioxide,e.params.pH,e.params.density,e.params.sulphates,e.feedback||"",e.note||"",e.humanDecision||""]);
  const blob=new Blob(["\uFEFF",[h,...rows].map(r=>r.join(",")).join("\n")],{type:"text/csv;charset=utf-8"});
  const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="vinum_loturi.csv"; a.click();
}

function exportTXT(result,params,wineType) {
  const lines=["RAPORT ANALIZĂ VIN — VINUM ANALYTICS","=".repeat(42),`Data: ${new Date().toLocaleString("ro-RO")}`,`Tip: ${wineType}`,`Scor: ${result.score}/10  |  Calificativ: ${result.calificativ}`,`Preț: ${result.pret_min}–${result.pret_max} RON  |  Vandabil: ${result.vandabil?"Da":"Nu"}`,result.ci_low?`Interval încredere 95%: [${result.ci_low} – ${result.ci_high}]`:""," ","PARAMETRI LABORATOR","-".repeat(30),...Object.entries(params).map(([k,v])=>`${paramLabels[k].label}: ${v} ${paramLabels[k].unit}`)," ","FACTORI POZITIVI","-".repeat(30),...(result.factori_pozitivi||[]).map(f=>`+ ${f}`)," ","FACTORI NEGATIVI","-".repeat(30),...(result.factori_negativi||[]).map(f=>`- ${f}`)," ","RECOMANDĂRI PRESCRIPTIVE","-".repeat(30),...(result.prescriptiv||[]).map(r=>`• ${r.param}: ${r.current} → ${r.target} (+${r.impact?.toFixed(1)} pt calitate)`)," ","DECIZIE SSD","-".repeat(30),result.rezumat_ssd||""," ","RECOMANDARE ENOLOG","-".repeat(30),result.recomandare||""," ","MARKETING","-".repeat(30),`Segment: ${result.marketing?.segment||""}`,`Canal: ${result.marketing?.canal_distributie||""}`,`Mesaj: ${result.marketing?.mesaj_marketing||""}`," ","Generat de Vinum Analytics • Bazat pe Cortez et al. (2009)"];
  const blob=new Blob([lines.join("\n")],{type:"text/plain;charset=utf-8"});
  const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="raport_vin.txt"; a.click();
}

export default function App() {
  const [params,setParams]=useState(defaultParams);
  const [wineType,setWineType]=useState("roșu");
  const [result,setResult]=useState(null);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState(null);
  const [tab,setTab]=useState("parametri");
  const [history,setHistory]=useState(loadHistory);
  const [feedback,setFeedback]=useState(null);
  const [feedbackNote,setFeedbackNote]=useState("");
  const [showFB,setShowFB]=useState(false);
  const [humanDecision,setHumanDecision]=useState(null);
  const [humanNote,setHumanNote]=useState("");
  const [humanSaved,setHumanSaved]=useState(false);
  const apiKey = "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";

  const bench = wineType==="alb"?BENCHMARK.white:wineType==="rosé"?BENCHMARK.rosé:BENCHMARK.red;

  const analyze = async () => {
    setLoading(true); setError(null); setResult(null);
    setHumanDecision(null); setHumanNote(""); setHumanSaved(false);
    setFeedback(null); setFeedbackNote(""); setShowFB(false);

    const prompt = `Ești un sistem AI expert în enologie bazat pe modelul Cortez et al. (2009). Analizează parametrii și returnează EXCLUSIV JSON valid, fără markdown.

Tip vin: ${wineType}
Parametri:
${Object.entries(params).map(([k,v])=>`- ${paramLabels[k].label}: ${v} ${paramLabels[k].unit}`).join("\n")}
Media dataset UCI (${wineType}): scor mediu ${bench.avg_score}/10, alcool ${bench.alcohol}%, acid.volatilă ${bench.volatile_acidity} g/L

JSON obligatoriu:
{
  "score": <1.0-10.0>,
  "calificativ": "<Slab|Mediocru|Bun|Foarte Bun|Excelent>",
  "pret_min": <RON întreg>,
  "pret_max": <RON întreg>,
  "vandabil": <true|false>,
  "ci_low": <score-0.3 până score-1.2>,
  "ci_high": <score+0.3 până score+1.2>,
  "uncertainty": "<scăzută|medie|ridicată>",
  "uncertainty_reason": "<1 propoziție motiv>",
  "vs_benchmark": "<1 propoziție comparație cu media UCI>",
  "factori_pozitivi": ["factor1","factor2"],
  "factori_negativi": ["factor1","factor2"],
  "shap": {"Alcool":<-2 la 2>,"Aciditate fixă":<nr>,"Aciditate volatilă":<nr>,"Acid citric":<nr>,"Zahăr rezidual":<nr>,"Cloruri":<nr>,"SO2 liber":<nr>,"SO2 total":<nr>,"pH":<nr>,"Densitate":<nr>,"Sulfați":<nr>},
  "prescriptiv": [{"param":"<parametru>","current":"<val+unitate>","target":"<val+unitate>","impact":<nr>,"action":"<acțiune scurtă>"}],
  "marketing": {"segment":"<Premium|Mediu|Economic>","profil_consumator":"<descriere>","canal_distributie":"<canal>","mesaj_marketing":"<slogan>"},
  "recomandare": "<2-3 propoziții producător>",
  "rezumat_ssd": "<APROBAT/RESPINS/CONDIȚIONAT + motiv scurt>"
}`;

    try {
      const resp = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          "Authorization": `Bearer ${apiKey.trim()}`,
          "HTTP-Referer": "http://localhost:3000",
          "X-Title": "Vinum Analytics",
        },
        body:JSON.stringify({
          model:"anthropic/claude-sonnet-4-5",
          max_tokens:1800,
          messages:[{role:"user",content:prompt}]
        })
      });
      const data = await resp.json();
      if(data.error) { setError(`Eroare API: ${data.error.message}`); setLoading(false); return; }
      const text = data.choices?.[0]?.message?.content || "";
      const clean = text.replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      setTab("rezultat");
      const entry={id:Date.now(),timestamp:new Date().toISOString(),wineType,params:{...params},result:parsed,feedback:null,note:"",humanDecision:null,humanNote:""};
      const newH=[entry,...loadHistory()];
      saveHistory(newH); setHistory(newH);
    } catch(e) {
      setError(`Eroare: ${e.message}. Verificați că API Key-ul OpenRouter este corect și aveți credite.`);
    } finally { setLoading(false); }
  };

  const submitFeedback = type => {
    setFeedback(type);
    const upd=history.map((h,i)=>i===0?{...h,feedback:type,note:feedbackNote}:h);
    saveHistory(upd); setHistory(upd); setShowFB(false);
  };
  const saveHuman = () => {
    setHumanSaved(true);
    const upd=history.map((h,i)=>i===0?{...h,humanDecision,humanNote}:h);
    saveHistory(upd); setHistory(upd);
  };

  const shapEntries = result?.shap?Object.entries(result.shap).sort((a,b)=>Math.abs(b[1])-Math.abs(a[1])):[];
  const maxAbs = shapEntries.length?Math.max(...shapEntries.map(([,v])=>Math.abs(v)),0.1):1;

  const Tab = (id,label,badge=null) => (
    <button key={id} onClick={()=>setTab(id)} style={{
      padding:"9px 16px",border:"none",background:"transparent",
      color:tab===id?G:"#6a5040",
      borderBottom:`2px solid ${tab===id?G:"transparent"}`,
      cursor:"pointer",fontSize:11,fontFamily:"inherit",letterSpacing:"0.06em",
      marginBottom:-1,whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5
    }}>
      {label}
      {badge&&<span style={{background:R,color:"#fff",borderRadius:3,fontSize:9,padding:"1px 5px",lineHeight:1.4}}>{badge}</span>}
    </button>
  );

  // === STYLES ===
  const bg = {minHeight:"100vh",width:"100%",background:"#0a0704",color:"#e8ddd0",fontFamily:"'Montserrat',sans-serif"};
  const btn = (col="#c8a96e",disabled=false) => ({
    padding:"5px 12px",borderRadius:3,border:`1px solid ${col}50`,
    background:`${col}15`,color:col,cursor:disabled?"not-allowed":"pointer",
    fontSize:11,fontFamily:"'Montserrat',sans-serif",opacity:disabled?0.5:1
  });

  return (
    <div style={bg}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html, body, #root { background: #0a0704 !important; margin: 0; padding: 0; width: 100%; }
        body { overflow-x: hidden; }
        * { font-family: 'Montserrat', sans-serif !important; }
        input[type=range] { -webkit-appearance:none; appearance:none; height:4px; border-radius:2px; outline:none; }
        input[type=range]::-webkit-slider-thumb { -webkit-appearance:none; width:16px; height:16px; border-radius:50%; background:#c8a96e; cursor:pointer; border:2px solid #0a0704; }
        input[type=range]::-moz-range-thumb { width:14px; height:14px; border-radius:50%; background:#c8a96e; cursor:pointer; border:2px solid #0a0704; }
      `}</style>

      {/* HEADER */}
      <div style={{borderBottom:"1px solid #2e1f0e",padding:"16px 28px",background:"#0e0905"}}>
        <div style={{maxWidth:980,margin:"0 auto",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <span style={{fontSize:24}}>🍷</span>
          <div style={{flex:1}}>
            <h1 style={{margin:0,fontSize:18,fontWeight:400,letterSpacing:"0.15em",color:G,textTransform:"uppercase"}}>Vinum Analytics</h1>
            <p style={{margin:"1px 0 0",fontSize:10,color:"#5a4030",letterSpacing:"0.1em"}}>SISTEM AI · CALITATE · PREȚ · XAI · SSD · MARKETING · DIGITAL TWIN</p>
          </div>
          <div style={{display:"flex",gap:6}}>
            {["roșu","alb","rosé"].map(t=>(
              <button key={t} onClick={()=>setWineType(t)} style={{padding:"4px 12px",borderRadius:3,border:`1px solid ${wineType===t?G:"#2e1f0e"}`,background:wineType===t?"#c8a96e18":"transparent",color:wineType===t?G:"#5a4030",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{maxWidth:980,margin:"0 auto",padding:"22px 28px"}}>

        {/* PRESETS */}
        <div style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:10,color:"#5a4030",letterSpacing:"0.1em"}}>EXEMPLE RAPIDE:</span>
          {Object.entries(presets).map(([k,p])=>(
            <button key={k} onClick={()=>{setParams(p.values);setResult(null);setError(null);setHumanDecision(null);setHumanNote("");setHumanSaved(false);}}
              style={{padding:"4px 12px",borderRadius:3,border:"1px solid #2e1f0e",background:"transparent",color:"#9a8070",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{p.label}</button>
          ))}
        </div>

        {/* TABS */}
        <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:"1px solid #2e1f0e",overflowX:"auto"}}>
          {Tab("parametri","Parametri")}
          {Tab("rezultat","Rezultat & XAI")}
          {Tab("benchmark","Benchmark")}
          {Tab("ssd","Decizie SSD")}
          {Tab("marketing","Marketing")}
          {Tab("fermentatie","Digital Twin")}
          {Tab("istoric","Istoric",history.length||null)}
        </div>

        {/* ═══ TAB: PARAMETRI ═══ */}
        {tab==="parametri"&&(
          <div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
              {Object.entries(paramLabels).map(([key,meta])=>{
                const pct = ((params[key]-meta.min)/(meta.max-meta.min)*100).toFixed(1);
                return (
                <div key={key} style={card()}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <label style={{fontSize:11,color:"#9a8070"}}>{meta.label}</label>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <input type="number" min={meta.min} max={meta.max} step={meta.step} value={params[key]}
                        onChange={e=>{const v=parseFloat(e.target.value);if(!isNaN(v)&&v>=meta.min&&v<=meta.max)setParams(p=>({...p,[key]:v}));}}
                        style={{width:68,padding:"3px 6px",background:"#0a0704",border:"1px solid #3a2a1a",borderRadius:3,color:G,fontSize:12,fontFamily:"monospace",textAlign:"right"}}/>
                      <span style={{fontSize:10,color:"#5a4030",width:32}}>{meta.unit}</span>
                    </div>
                  </div>
                  <input type="range" min={meta.min} max={meta.max} step={meta.step} value={params[key]}
                    onChange={e=>setParams(p=>({...p,[key]:parseFloat(e.target.value)}))}
                    style={{width:"100%",cursor:"pointer",accentColor:G,background:`linear-gradient(to right, ${G} ${pct}%, #2e1f0e ${pct}%)`}}/>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#3a2a1a",marginTop:3}}>
                    <span>{meta.min}</span><span>{meta.max}</span>
                  </div>
                </div>
              )})}
            </div>
            <button onClick={analyze} disabled={loading} style={{
              width:"100%",padding:"14px",
              background:loading?"#1e1208":`linear-gradient(135deg,#7a5020,${G},#7a5020)`,
              border:"none",color:loading?"#5a4030":"#0a0704",fontSize:13,letterSpacing:"0.18em",
              textTransform:"uppercase",cursor:loading?"not-allowed":"pointer",borderRadius:4,
              fontFamily:"Georgia,serif",fontWeight:700,boxShadow:loading?"none":`0 4px 20px ${G}20`
            }}>
              {loading?"⏳  Analizez vinul...":"Estimează Calitatea & Prețul"}
            </button>
            {error&&<div style={{marginTop:12,padding:"12px 16px",background:"#1a0808",border:`1px solid ${R}60`,borderRadius:4,color:R,fontSize:12,lineHeight:1.6}}>{error}</div>}
          </div>
        )}

        {/* ═══ TAB: REZULTAT & XAI ═══ */}
        {tab==="rezultat"&&(
          <div>
            {!result?(
              <div style={{textAlign:"center",padding:"50px 0",color:"#3a2a1a"}}>
                <p style={{fontSize:36,margin:"0 0 12px"}}>🍷</p>
                <p style={{fontSize:12}}>Introduceți parametrii și apăsați „Estimează".</p>
                <button onClick={()=>setTab("parametri")} style={{...btn(),marginTop:12}}>Mergi la Parametri</button>
              </div>
            ):(
              <div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  {/* Scor */}
                  <div style={{...card(),borderLeft:`4px solid ${calCol[result.calificativ]||G}`}}>
                    <p style={{margin:"0 0 2px",fontSize:10,color:"#5a4030",letterSpacing:"0.12em"}}>SCOR CALITATE ESTIMAT</p>
                    <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                      <span style={{fontSize:44,fontWeight:400,color:calCol[result.calificativ]||G,lineHeight:1}}>{result.score}</span>
                      <span style={{fontSize:16,color:"#3a2a1a"}}>/10</span>
                    </div>
                    <ScoreBar score={result.score}/>
                    {result.ci_low&&(
                      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginTop:6}}>
                        <span style={{fontSize:10,color:"#5a4030"}}>IC 95%:</span>
                        <span style={{fontSize:11,fontFamily:"monospace",color:BL}}>[{result.ci_low} – {result.ci_high}]</span>
                        <span style={{fontSize:10,padding:"1px 7px",borderRadius:2,background:`${result.uncertainty==="scăzută"?GR:result.uncertainty==="medie"?AM:R}18`,color:result.uncertainty==="scăzută"?GR:result.uncertainty==="medie"?AM:R}}>
                          Incertitudine {result.uncertainty}
                        </span>
                      </div>
                    )}
                    {result.uncertainty_reason&&<p style={{margin:"5px 0 0",fontSize:11,color:"#9a8070",lineHeight:1.5}}>{result.uncertainty_reason}</p>}
                    <span style={{display:"inline-block",marginTop:8,padding:"2px 10px",borderRadius:3,background:`${calCol[result.calificativ]||G}18`,border:`1px solid ${calCol[result.calificativ]||G}40`,color:calCol[result.calificativ]||G,fontSize:11}}>{result.calificativ}</span>
                  </div>
                  {/* Pret */}
                  <div style={card()}>
                    <p style={{margin:"0 0 2px",fontSize:10,color:"#5a4030",letterSpacing:"0.12em"}}>PREȚ ESTIMAT</p>
                    <p style={{margin:"4px 0",fontSize:28,color:G,fontWeight:400}}>{result.pret_min}–{result.pret_max} <span style={{fontSize:14}}>RON</span></p>
                    <p style={{margin:"2px 0 12px",fontSize:10,color:"#5a4030"}}>per sticlă 750ml · vin {wineType}</p>
                    <div style={{padding:"8px 12px",background:result.vandabil?"#8fbe6a12":"#c0505012",border:`1px solid ${result.vandabil?GR+"40":R+"40"}`,borderRadius:3}}>
                      <span style={{fontSize:12,color:result.vandabil?GR:R}}>{result.vandabil?"✓ Recomandat pentru vânzare":"⚠ Nerecomandat pentru vânzare directă"}</span>
                    </div>
                  </div>
                </div>

                {/* Factori */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  {[[GR,"✓ FACTORI POZITIVI",result.factori_pozitivi,true],[R,"✗ FACTORI NEGATIVI",result.factori_negativi,false]].map(([col,title,items,pos])=>(
                    <div key={title} style={card()}>
                      <p style={{margin:"0 0 8px",fontSize:10,color:col,letterSpacing:"0.12em"}}>{title}</p>
                      {(items||[]).map((f,i)=>(
                        <div key={i} style={{display:"flex",gap:7,padding:"4px 0",borderBottom:"1px solid #0e0904"}}>
                          <span style={{color:col,flexShrink:0}}>{pos?"✓":"✗"}</span>
                          <span style={{fontSize:11,color:"#c8b8a0",lineHeight:1.5}}>{f}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* SHAP */}
                {shapEntries.length>0&&(
                  <div style={{...card(),marginBottom:12}}>
                    <p style={{margin:"0 0 4px",fontSize:10,color:G,letterSpacing:"0.12em"}}>CONTRIBUȚIA PARAMETRILOR — XAI (VALORI SHAP)</p>
                    <p style={{margin:"0 0 10px",fontSize:11,color:"#6a5040"}}>Cât contribuie fiecare parametru la scorul final față de media de {bench.avg_score}</p>
                    <div style={{display:"flex",gap:14,marginBottom:10,fontSize:11,color:"#5a4030"}}>
                      <span><span style={{color:GR}}>■</span> Contribuție pozitivă</span>
                      <span><span style={{color:R}}>■</span> Contribuție negativă</span>
                    </div>
                    {shapEntries.map(([l,v])=><ShapBar key={l} label={l} value={v} maxAbs={maxAbs}/>)}
                  </div>
                )}

                {/* Prescriptiv */}
                {result.prescriptiv?.length>0&&(
                  <div style={{...card(),marginBottom:12}}>
                    <p style={{margin:"0 0 12px",fontSize:10,color:AM,letterSpacing:"0.12em"}}>RECOMANDĂRI PRESCRIPTIVE — CE SĂ MODIFICAȚI</p>
                    {result.prescriptiv.map((r,i)=>(
                      <div key={i} style={{background:"#0e0904",border:"1px solid #2e1f0e",borderRadius:4,padding:"9px 13px",marginBottom:7}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:13,color:"#e8ddd0",fontWeight:600}}>{r.param}</span>
                          <span style={{fontSize:11,color:G}}>+{r.impact?.toFixed(1)} pt calitate</span>
                        </div>
                        <div style={{display:"flex",gap:8,alignItems:"center",fontSize:12,color:"#9a8070"}}>
                          <span style={{color:R}}>{r.current}</span><span>→</span><span style={{color:GR}}>{r.target}</span>
                          <span style={{marginLeft:"auto",color:"#6a5040",fontSize:11}}>{r.action}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{...card(),marginBottom:12}}>
                  <p style={{margin:"0 0 7px",fontSize:10,color:G,letterSpacing:"0.12em"}}>RECOMANDARE ENOLOG</p>
                  <p style={{margin:0,fontSize:13,color:"#c8b8a0",lineHeight:1.7}}>{result.recomandare}</p>
                </div>

                {/* Feedback */}
                <div style={{...card(),marginBottom:12}}>
                  <p style={{margin:"0 0 9px",fontSize:10,color:"#5a4030",letterSpacing:"0.1em"}}>FEEDBACK ENOLOG — SCOR REAL (ÎNVĂȚARE CONTINUĂ)</p>
                  {feedback?(
                    <p style={{margin:0,fontSize:12,color:GR}}>✓ Feedback: {feedback==="corect"?"Predicție corectă":feedback==="prea_mare"?"Scor prea mare":"Scor prea mic"}{feedbackNote?` — "${feedbackNote}"`:""}</p>
                  ):(
                    <div>
                      <div style={{display:"flex",gap:7,marginBottom:7,flexWrap:"wrap"}}>
                        {[["corect","✓ Corect",GR],["prea_mare","↑ Prea mare",AM],["prea_mic","↓ Prea mic",OR]].map(([val,lbl,col])=>(
                          <button key={val} onClick={()=>{setFeedback(val);setShowFB(true);}} style={btn(col)}>{lbl}</button>
                        ))}
                      </div>
                      {showFB&&(
                        <div style={{marginTop:7}}>
                          <input placeholder="Scor real din degustare (opțional)" value={feedbackNote} onChange={e=>setFeedbackNote(e.target.value)}
                            style={{width:"100%",padding:"6px 10px",background:"#0a0704",border:"1px solid #3a2a1a",borderRadius:3,color:"#e8ddd0",fontSize:11,fontFamily:"inherit",boxSizing:"border-box"}}/>
                          <button onClick={()=>submitFeedback(feedback)} style={{marginTop:6,...btn(G),padding:"6px 16px",fontWeight:700}}>Salvează Feedback</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>exportTXT(result,params,wineType)} style={{...btn(),flex:1,padding:"9px"}}>⬇ Export Raport TXT</button>
                  <button onClick={()=>exportCSV(history)} disabled={!history.length} style={{...btn("#9a8070",!history.length),flex:1,padding:"9px"}}>⬇ Export Istoric CSV</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB: BENCHMARK ═══ */}
        {tab==="benchmark"&&(
          <div>
            {!result?(
              <div style={{textAlign:"center",padding:"50px 0",color:"#3a2a1a"}}>
                <p style={{fontSize:12}}>Rulați mai întâi o analiză.</p>
                <button onClick={()=>setTab("parametri")} style={{...btn(),marginTop:12}}>Mergi la Parametri</button>
              </div>
            ):(
              <div>
                <div style={{...card({borderLeft:`4px solid ${BL}`,marginBottom:14})}}>
                  <p style={{margin:"0 0 4px",fontSize:10,color:"#5a4030",letterSpacing:"0.12em"}}>COMPARAȚIE CU MEDIA DATASET UCI — VIN {wineType.toUpperCase()}</p>
                  <p style={{margin:"0 0 12px",fontSize:12,color:"#c8b8a0",lineHeight:1.6}}>{result.vs_benchmark}</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                    {[["Scor lot curent",`${result.score}/10`,calCol[result.calificativ]||G],["Scor mediu UCI",`${bench.avg_score}/10`,"#9a8070"]].map(([l,v,c])=>(
                      <div key={l} style={{background:"#0e0904",borderRadius:4,padding:"10px 12px",border:"1px solid #2e1f0e"}}>
                        <p style={{margin:"0 0 3px",fontSize:10,color:"#5a4030"}}>{l}</p>
                        <p style={{margin:0,fontSize:20,color:c,fontFamily:"monospace"}}>{v}</p>
                      </div>
                    ))}
                  </div>
                  <div style={{borderTop:"1px solid #2e1f0e",paddingTop:12}}>
                    <div style={{display:"flex",gap:6,fontSize:10,color:"#5a4030",marginBottom:6}}>
                      <span style={{width:150,textAlign:"right"}}>PARAMETRU</span>
                      <span style={{width:90,textAlign:"right"}}>LOT MEU</span>
                      <span style={{width:90,textAlign:"right"}}>MEDIE UCI</span>
                      <span style={{width:70,textAlign:"right"}}>DIFERENȚĂ</span>
                    </div>
                    {[["alcohol","Alcool","%"],["fixed_acidity","Aciditate fixă","g/L"],["volatile_acidity","Aciditate volatilă","g/L"],["citric_acid","Acid citric","g/L"],["residual_sugar","Zahăr rezidual","g/L"],["chlorides","Cloruri","g/L"],["sulphates","Sulfați","g/L"],["pH","pH",""]].map(([key,label,unit])=>{
                      const diff=params[key]-bench[key], dp=paramLabels[key].dp;
                      const col=Math.abs(diff)<0.05*(bench[key]||1)?GR:diff>0?AM:OR;
                      return (
                        <div key={key} style={{display:"flex",gap:6,padding:"5px 0",borderBottom:"1px solid #0e0904",alignItems:"center"}}>
                          <span style={{width:150,fontSize:11,color:"#9a8070",textAlign:"right"}}>{label}</span>
                          <span style={{width:90,fontSize:12,fontFamily:"monospace",color:"#e8ddd0",textAlign:"right"}}>{params[key].toFixed(dp)} {unit}</span>
                          <span style={{width:90,fontSize:11,color:"#5a4030",textAlign:"right"}}>{bench[key].toFixed(dp)} {unit}</span>
                          <span style={{width:70,fontSize:11,fontFamily:"monospace",color:col,textAlign:"right"}}>{diff>0?"+":""}{diff.toFixed(dp)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{...card({fontSize:11,color:"#5a4030",lineHeight:1.7})}}>
                  <strong style={{color:"#9a8070"}}>Sursă:</strong> Cortez et al. (2009). Modeling wine preferences by data mining from physicochemical properties. <em>Decision Support Systems</em>, 47(4), 547–553. UCI Machine Learning Repository.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB: SSD ═══ */}
        {tab==="ssd"&&(
          <div>
            {!result?(
              <div style={{textAlign:"center",padding:"50px 0",color:"#3a2a1a"}}>
                <p style={{fontSize:12}}>Rulați mai întâi o analiză.</p>
                <button onClick={()=>setTab("parametri")} style={{...btn(),marginTop:12}}>Mergi la Parametri</button>
              </div>
            ):(
              <div>
                <div style={{...card({borderLeft:`4px solid ${result.vandabil?GR:R}`,marginBottom:12})}}>
                  <p style={{margin:"0 0 4px",fontSize:10,color:"#5a4030",letterSpacing:"0.12em"}}>RECOMANDARE AI — SISTEM SUPORT DECIZIE</p>
                  <p style={{margin:"6px 0",fontSize:17,color:result.vandabil?GR:R,lineHeight:1.4}}>{result.rezumat_ssd}</p>
                  <p style={{margin:0,fontSize:12,color:"#c8b8a0",lineHeight:1.6}}>{result.recomandare}</p>
                </div>
                <div style={{...card({marginBottom:12})}}>
                  <p style={{margin:"0 0 12px",fontSize:10,color:G,letterSpacing:"0.12em"}}>SINTEZĂ LOT</p>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:9}}>
                    {[["Calitate",`${result.score}/10`,calCol[result.calificativ]||G],["Calificativ",result.calificativ,calCol[result.calificativ]||G],["Preț",`${result.pret_min}–${result.pret_max} RON`,G],["Tip vin",wineType,"#9a8070"],["Alcool",`${params.alcohol}%`,params.alcohol>=10.5?GR:R],["Acid. vol.",`${params.volatile_acidity}`,params.volatile_acidity<=0.5?GR:R],["Incertitudine",result.uncertainty||"—",result.uncertainty==="scăzută"?GR:result.uncertainty==="medie"?AM:R],["vs Benchmark",result.score>bench.avg_score?"Peste medie":"Sub medie",result.score>bench.avg_score?GR:OR],["Segment mkt",result.marketing?.segment||"—",BL]].map(([l,v,c])=>(
                      <div key={l} style={{background:"#0e0904",borderRadius:4,padding:"9px 11px",border:"1px solid #2e1f0e"}}>
                        <p style={{margin:"0 0 3px",fontSize:10,color:"#5a4030"}}>{l}</p>
                        <p style={{margin:0,fontSize:12,color:c,fontFamily:"monospace"}}>{v}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {result.prescriptiv?.length>0&&(
                  <div style={{...card({marginBottom:12})}}>
                    <p style={{margin:"0 0 10px",fontSize:10,color:AM,letterSpacing:"0.12em"}}>ACȚIUNI ÎNAINTE DE ÎMBUTELIERE</p>
                    {result.prescriptiv.map((r,i)=>(
                      <div key={i} style={{display:"flex",gap:10,padding:"7px 0",borderBottom:"1px solid #0e0904",alignItems:"center"}}>
                        <span style={{background:AM+"22",color:AM,borderRadius:2,padding:"1px 7px",fontSize:10,flexShrink:0}}>#{i+1}</span>
                        <span style={{fontSize:12,color:"#e8ddd0",flex:1}}>{r.param}: <span style={{color:R}}>{r.current}</span> → <span style={{color:GR}}>{r.target}</span></span>
                        <span style={{fontSize:11,color:G,flexShrink:0}}>+{r.impact?.toFixed(1)} pt</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* FLUX HIBRID */}
                <div style={{...card({marginBottom:12})}}>
                  <p style={{margin:"0 0 8px",fontSize:10,color:BL,letterSpacing:"0.12em"}}>DECIZIE ENOLOG — FLUX DECIZIONAL HIBRID (AI + OM)</p>
                  <p style={{margin:"0 0 12px",fontSize:11,color:"#6a5040"}}>Confirmați sau suprascrieți decizia AI. Ambele decizii se înregistrează.</p>
                  {humanSaved?(
                    <div style={{padding:"10px 14px",background:`${humanDecision==="aprobare"?GR:humanDecision==="respingere"?R:AM}12`,border:`1px solid ${humanDecision==="aprobare"?GR:humanDecision==="respingere"?R:AM}40`,borderRadius:4}}>
                      <p style={{margin:"0 0 3px",fontSize:13,color:humanDecision==="aprobare"?GR:humanDecision==="respingere"?R:AM,fontWeight:600}}>
                        {humanDecision==="aprobare"?"✓ APROBAT de enolog":humanDecision==="respingere"?"✗ RESPINS de enolog":"⚠ CONDIȚIONAT de enolog"}
                      </p>
                      {humanNote&&<p style={{margin:0,fontSize:11,color:"#9a8070"}}>Motivare: {humanNote}</p>}
                    </div>
                  ):(
                    <div>
                      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                        {[["aprobare","✓ Aprob",GR],["respingere","✗ Resping",R],["conditionat","⚠ Condiționat",AM]].map(([val,lbl,col])=>(
                          <button key={val} onClick={()=>setHumanDecision(val)} style={{...btn(col),border:`2px solid ${humanDecision===val?col:col+"40"}`,fontWeight:humanDecision===val?700:400,padding:"7px 16px"}}>{lbl}</button>
                        ))}
                      </div>
                      {humanDecision&&(
                        <div>
                          <input placeholder="Motivare enolog..." value={humanNote} onChange={e=>setHumanNote(e.target.value)}
                            style={{width:"100%",padding:"7px 10px",background:"#0a0704",border:"1px solid #3a2a1a",borderRadius:3,color:"#e8ddd0",fontSize:11,fontFamily:"inherit",boxSizing:"border-box",marginBottom:7}}/>
                          <button onClick={saveHuman} style={{...btn(G),padding:"7px 18px",fontWeight:700}}>Înregistrează Decizia</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{...card({fontSize:11,color:"#5a4030",lineHeight:1.6})}}>
                  <strong style={{color:"#9a8070"}}>Notă:</strong> SSD pe 3 straturi (Cap. 3.3). Decizia finală aparține enologului.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB: MARKETING ═══ */}
        {tab==="marketing"&&(
          <div>
            {!result?.marketing?(
              <div style={{textAlign:"center",padding:"50px 0",color:"#3a2a1a"}}>
                <p style={{fontSize:12}}>Rulați mai întâi o analiză.</p>
                <button onClick={()=>setTab("parametri")} style={{...btn(),marginTop:12}}>Mergi la Parametri</button>
              </div>
            ):(
              <div>
                <div style={{...card({borderLeft:`4px solid ${BL}`,marginBottom:12})}}>
                  <p style={{margin:"0 0 4px",fontSize:10,color:"#5a4030",letterSpacing:"0.12em"}}>PROFIL MARKETING AI</p>
                  <p style={{margin:"6px 0 4px",fontSize:20,color:BL}}>{result.marketing.segment}</p>
                  <p style={{margin:"4px 0 0",fontSize:13,color:"#c8b8a0",lineHeight:1.6,fontStyle:"italic"}}>„{result.marketing.mesaj_marketing}"</p>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  {[["Segment de piață",result.marketing.segment,BL],["Canal de distribuție",result.marketing.canal_distributie,G]].map(([l,v,c])=>(
                    <div key={l} style={card()}>
                      <p style={{margin:"0 0 5px",fontSize:10,color:"#5a4030",letterSpacing:"0.1em"}}>{l.toUpperCase()}</p>
                      <p style={{margin:0,fontSize:15,color:c}}>{v}</p>
                    </div>
                  ))}
                </div>
                <div style={{...card({marginBottom:12})}}>
                  <p style={{margin:"0 0 8px",fontSize:10,color:BL,letterSpacing:"0.12em"}}>PROFIL CONSUMATOR ȚINTĂ</p>
                  <p style={{margin:0,fontSize:13,color:"#c8b8a0",lineHeight:1.7}}>{result.marketing.profil_consumator}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ TAB: DIGITAL TWIN ═══ */}
        {tab==="fermentatie"&&(
          <div>
            <div style={{...card({borderLeft:`4px solid ${G}`,marginBottom:16})}}>
              <p style={{margin:"0 0 4px",fontSize:10,color:"#5a4030",letterSpacing:"0.12em"}}>SIMULARE DIGITAL TWIN — MONITORIZARE FERMENTAȚIE</p>
              <p style={{margin:0,fontSize:11,color:"#9a8070",lineHeight:1.6}}>Modelează evoluția zahărului și alcoolului pe 14 zile. Bazat pe cinetică Monod adaptată.</p>
            </div>
            <FermentationSim/>
          </div>
        )}

        {/* ═══ TAB: ISTORIC ═══ */}
        {tab==="istoric"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <span style={{fontSize:11,color:"#5a4030"}}>{history.length} loturi analizate</span>
              <div style={{display:"flex",gap:8}}>
                {history.length>0&&<button onClick={()=>exportCSV(history)} style={{...btn(),padding:"5px 12px"}}>⬇ Export CSV</button>}
                {history.length>0&&<button onClick={()=>{localStorage.removeItem(HISTORY_KEY);setHistory([]);}} style={{...btn(R),padding:"5px 12px"}}>Șterge tot</button>}
              </div>
            </div>
            {history.length===0?(
              <div style={{textAlign:"center",padding:"50px 0",color:"#3a2a1a"}}><p>Niciun lot analizat încă.</p></div>
            ):(
              history.map((h,i)=>{
                const d=new Date(h.timestamp);
                const ds=d.toLocaleDateString("ro-RO")+" "+d.toLocaleTimeString("ro-RO",{hour:"2-digit",minute:"2-digit"});
                const cc=calCol[h.result?.calificativ]||G;
                return (
                  <div key={h.id} style={{...card({borderLeft:`3px solid ${cc}`,marginBottom:9,cursor:"pointer"})}}
                    onClick={()=>{setParams(h.params);setWineType(h.wineType);setResult(h.result);setFeedback(h.feedback||null);setFeedbackNote(h.note||"");setHumanDecision(h.humanDecision||null);setHumanNote(h.humanNote||"");setHumanSaved(!!h.humanDecision);setTab("rezultat");}}>
                    <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
                      <div>
                        <span style={{fontSize:10,color:"#5a4030"}}>#{history.length-i} · {ds} · Vin {h.wineType}</span>
                        <div style={{marginTop:4,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                          <span style={{fontSize:17,color:cc,fontFamily:"monospace"}}>{h.result?.score}/10</span>
                          <span style={{fontSize:11,color:cc}}>{h.result?.calificativ}</span>
                          <span style={{fontSize:11,color:"#5a4030"}}>{h.result?.pret_min}–{h.result?.pret_max} RON</span>
                          <span style={{fontSize:10,color:h.result?.vandabil?GR:R}}>{h.result?.vandabil?"✓":"✗"}</span>
                        </div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"flex-end"}}>
                        {h.feedback&&<span style={{fontSize:10,padding:"1px 7px",borderRadius:2,background:`${h.feedback==="corect"?GR:AM}15`,color:h.feedback==="corect"?GR:AM}}>FB: {h.feedback}</span>}
                        {h.humanDecision&&<span style={{fontSize:10,padding:"1px 7px",borderRadius:2,background:`${h.humanDecision==="aprobare"?GR:h.humanDecision==="respingere"?R:AM}15`,color:h.humanDecision==="aprobare"?GR:h.humanDecision==="respingere"?R:AM}}>Enolog: {h.humanDecision}</span>}
                      </div>
                    </div>
                    <div style={{marginTop:7,display:"flex",gap:6,flexWrap:"wrap"}}>
                      {[["Alcool",h.params.alcohol,"%"],["Acid.vol.",h.params.volatile_acidity,"g/L"],["pH",h.params.pH,""],["Sulfați",h.params.sulphates,"g/L"]].map(([l,v,u])=>(
                        <span key={l} style={{fontSize:10,color:"#5a4030",background:"#0e0904",padding:"2px 7px",borderRadius:2}}>{l}: {v}{u}</span>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

      </div>
    </div>
  );
}
